<script setup lang="ts">
import type { Popover } from 'primevue';
import { debounce } from 'lodash-es';
import { ref, toRef } from 'vue';
import { ColorPicker } from 'vue-accessible-color-picker';

import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { watchImmediate } from '@vueuse/core';

import type {
  ColorPickerChangeEvent,
  RgbaObj,
  VFormColorInputEmits,
  VFormColorInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormColorInputProps>(), {
  format: 'hex',
});
const emit = defineEmits<VFormColorInputEmits>();

const pickerOverlay = ref<InstanceType<typeof Popover>>();

const { innerValue, setValue } = useInputInnerValue(toRef(props, 'value'));

const currentCssColor = ref('#ffffff');
const currentColor = ref<ColorPickerChangeEvent>();

function togglePickerOverlay(event: MouseEvent): void {
  if (props.disabled || props.readonly) {
    return;
  }
  pickerOverlay.value?.toggle(event);
}

function onBlur(): void {
  emit('blur', innerValue.value);
}

function handlePickerChange(data: ColorPickerChangeEvent): void {
  currentCssColor.value = data.cssColor;
  currentColor.value = data;

  if (!currentColor.value)
    return;
  innerValue.value = getFormattedValue(currentColor.value);
  setValue(innerValue.value);
  emit('change', innerValue.value);
}

const handlePickerChangeDebounced = debounce(handlePickerChange, 200);

watchImmediate(innerValue, (newVal) => {
  if (newVal && validateCssString(newVal)) {
    currentCssColor.value = newVal;
  }
});

function getFormattedValue(value: ColorPickerChangeEvent): string {
  if (props.format === 'rgb') {
    const { rgb } = value.colors;
    return getRgbString(rgb);
  }
  if (props.format === 'rgba') {
    const { rgb } = value.colors;
    return getRgbaString(rgb);
  }

  return value.cssColor;
}

function convertRgbComponent(component: number): number {
  return Math.round(component * 255);
}

function getRgbString(rgb: RgbaObj): string {
  return `rgb(${convertRgbComponent(rgb.r)},${convertRgbComponent(rgb.g)},${convertRgbComponent(rgb.b)})`;
}

function getRgbaString(rgb: RgbaObj): string {
  return `rgba(${convertRgbComponent(rgb.r)},${convertRgbComponent(rgb.g)},${convertRgbComponent(rgb.b)},${rgb.a})`;
}

function validateCssString(value: string): boolean {
  const o = new Option().style;
  o.color = value;
  return o.color !== '';
}
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormColorInput'"
    :errors="errors"
    :hide-errors="hideErrors"
    :readonly="readonly"
    class="relative"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    is-not-filled
    @click="togglePickerOverlay"
  >
    <div class="p-input-filled">
      <VFormFloatLabelInputWrapper
        :name="name"
        :label="label"
        :required="required"
        :readonly="readonly"
      >
        <PInputText
          v-model="innerValue"
          :name="name"
          :placeholder="placeholder"
          type="text"
          :required="required"
          :input-id="name"
          readonly
          :disabled="disabled"
          class="w-full"
          :class="{ 'p-invalid': isInvalid && !readonly }"
          :autocomplete="autocomplete ? autocomplete : 'off'"
          @blur="onBlur"
          @focus="emit('focus', innerValue)"
          @change="emit('change', innerValue)"
          @input="emit('input', innerValue)"
        />
      </VFormFloatLabelInputWrapper>
    </div>
    <PButton
      class="!absolute bottom-3.5 right-[10px] z-10 size-8 min-w-0 p-0 translate-3d-y-[-50%]"
      :style="{ 'background-color': currentCssColor }"
      text
      outlined
    />
    <POverlayPanel
      ref="pickerOverlay"
      :pt="{ content: { class: 'p-4' } }"
      @hide="onBlur"
    >
      <ColorPicker
        type="string"
        :color="currentCssColor"
        :visible-formats="[format === 'hex' ? 'hex' : 'rgb']"
        :alpha-channel="format === 'rgba' ? 'show' : 'hide'"
        @color-change="handlePickerChangeDebounced"
      />
    </POverlayPanel>
  </VFormInputWrapper>
</template>

<style scoped lang="scss">
:deep(.vacp-copy-button) {
  display: none;
}
</style>
